import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {toast} from "react-toastify";
import {SignInContext} from "./SignInContext";
import {LanguageRepository} from "../repositories/LanguageRepository";
import {LlmModelRepository} from "../repositories/LlmModelRepository";
import {TranscribeModelRepository} from "../repositories/TranscribeModelRepository";
import {VoiceModelRepository} from "../repositories/VoiceModelRepository";
import {BackendConfigurationRepository} from "../repositories/BackendConfigurationRepository";

// Reference data loaded once after login and shared across pages.
// Admin-only lists are loaded only for admins, since only admins can open their pages.
const RESOURCES = {
  languages: {fetch: () => LanguageRepository.getAll(), adminOnly: false},
  llmModels: {fetch: () => LlmModelRepository.getAll(), adminOnly: true},
  transcribeModels: {fetch: () => TranscribeModelRepository.getAll(), adminOnly: true},
  voiceModels: {fetch: () => VoiceModelRepository.getAll(), adminOnly: true},
  // public models plus the models of the user's organizations, used when configuring projects
  myLlmModels: {fetch: () => LlmModelRepository.getMyModels(), adminOnly: false},
  myTranscribeModels: {fetch: () => TranscribeModelRepository.getMyModels(), adminOnly: false},
  myVoiceModels: {fetch: () => VoiceModelRepository.getMyModels(), adminOnly: false},
  myBackendConfigurations: {fetch: () => BackendConfigurationRepository.getMyConfigurations(), adminOnly: false},
};

// `loaded` means a fetch has finished (successfully or not), so pages stop showing a spinner
const EMPTY_CATALOG = Object.fromEntries(Object.keys(RESOURCES).map(key => [key, {items: [], loaded: false}]));

export const CatalogContext = React.createContext({
  catalog: EMPTY_CATALOG,
  refresh: () => Promise.resolve([]),
  setItems: () => {
  },
});

export const CatalogContextProvider = ({children}) => {
  const {isAuth, currentUser} = useContext(SignInContext);
  const isAdmin = currentUser?.roles?.includes("admin");

  const [catalog, setCatalog] = useState(EMPTY_CATALOG);
  // one request per resource at a time; pages mounting during the initial load reuse it
  const inFlight = useRef({});
  // bumped on logout so responses for the previous user are dropped
  const generation = useRef(0);

  const refresh = useCallback((key) => {
    if (!inFlight.current[key]) {
      const requestGeneration = generation.current;
      const isCurrent = () => requestGeneration === generation.current;
      inFlight.current[key] = RESOURCES[key].fetch()
        .then(response => {
          const items = response.data || [];
          if (isCurrent()) setCatalog(prev => ({...prev, [key]: {items, loaded: true}}));
          return items;
        })
        .catch(error => {
          if (isCurrent()) setCatalog(prev => ({...prev, [key]: {...prev[key], loaded: true}}));
          throw error;
        })
        .finally(() => {
          delete inFlight.current[key];
        });
    }
    return inFlight.current[key];
  }, []);

  const setItems = useCallback((key, updater) => {
    setCatalog(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        items: typeof updater === "function" ? updater(prev[key].items) : updater,
      },
    }));
  }, []);

  useEffect(() => {
    if (!isAuth || !currentUser) {
      generation.current += 1;
      inFlight.current = {};
      setCatalog(EMPTY_CATALOG);
      return;
    }
    Object.keys(RESOURCES)
      .filter(key => isAdmin || !RESOURCES[key].adminOnly)
      .forEach(key => refresh(key).catch(error => console.error(error)));
  }, [isAuth, currentUser?.username, isAdmin]);

  return (
    <CatalogContext.Provider value={{catalog, refresh, setItems}}>
      {children}
    </CatalogContext.Provider>
  );
};

// Cached items of one catalog resource. With `refreshOnMount` the page shows the cached items
// immediately and refetches in the background, updating the context when the data changed.
export const useLlmModels = () => {
  const {items} = useCatalog("myLlmModels");
  return {models: items, modelIds: [...new Set(items.map(m => m.modelId))]};
};

// Stores the chosen model id and the model's whole registry record as `llm_model` on the node.
// The api key is left out: the node JSON lives in Firebase.
export const selectNodeModel = (onChangeData, models, modelId) => {
  const model = models.find(m => m.modelId === modelId);
  const {apiKey, ...llmModel} = model || {};
  onChangeData("model", modelId);
  onChangeData("llm_model", model ? llmModel : null);
};

export const useCatalog = (key, {refreshOnMount = false} = {}) => {
  const {catalog, refresh, setItems} = useContext(CatalogContext);
  const {items, loaded} = catalog[key];

  useEffect(() => {
    if (!refreshOnMount) return;
    refresh(key).catch(error => {
      console.error(error);
      toast.error(error?.response?.data?.message || "Failed to refresh data");
    });
  }, [key, refreshOnMount, refresh]);

  return {
    items,
    loading: !loaded,
    refresh: useCallback(() => refresh(key), [key, refresh]),
    setItems: useCallback((updater) => setItems(key, updater), [key, setItems]),
  };
};
